import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Layers, Plus } from "lucide-react";

export default function Bins() {
  const [bins, setBins] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ warehouse_id: "", code: "", zone: "", rack: "", level: "", capacity: 100 });
  const [filterWh, setFilterWh] = useState("__ALL__");

  const load = async () => {
    const url = filterWh && filterWh !== "__ALL__" ? `/warehouse/bins?warehouse_id=${filterWh}` : "/warehouse/bins";
    const [b, w] = await Promise.all([api.get(url), api.get("/inventory/warehouses")]);
    setBins(b.data); setWarehouses(w.data);
  };
  useEffect(() => { load(); }, [filterWh]);

  const save = async () => {
    try { await api.post("/warehouse/bins", form); toast.success("Bin added"); setOpen(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // group bins by zone for visual heatmap
  const byZone = bins.reduce((acc, b) => {
    (acc[b.zone] = acc[b.zone] || []).push(b);
    return acc;
  }, {});

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="warehouse-bins">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black flex items-center gap-2">
            <Layers className="w-7 h-7 text-primary" /> Bins & Locations
          </h1>
          <p className="text-muted-foreground">Storage locations organized by zone, rack and level.</p>
        </div>
        <div className="flex gap-2 items-end">
          <Select value={filterWh} onValueChange={setFilterWh}>
            <SelectTrigger className="w-56" data-testid="bin-filter-wh"><SelectValue placeholder="All warehouses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__ALL__">All warehouses</SelectItem>
              {warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={() => setOpen(true)} data-testid="bin-new"><Plus className="w-4 h-4 mr-1" /> Add bin</Button>
        </div>
      </div>

      {Object.keys(byZone).length > 0 && (
        <div className="space-y-3">
          {Object.entries(byZone).map(([zone, list]) => (
            <div key={zone} className="surface-card p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="label-overline">Zone {zone}</div>
                <span className="text-xs text-muted-foreground">{list.length} bins</span>
              </div>
              <div className="grid grid-cols-6 sm:grid-cols-10 gap-1.5">
                {list.map((b) => (
                  <div key={b.id} className="rounded-md border border-border bg-muted/40 text-[10px] font-mono text-center py-1.5" title={`${b.code} · ${b.warehouse_name}`}>
                    {b.code}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Code</TableHead><TableHead>Warehouse</TableHead><TableHead>Zone</TableHead>
            <TableHead>Rack</TableHead><TableHead>Level</TableHead><TableHead className="text-right">Capacity</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {bins.slice(0, 60).map((b) => (
              <TableRow key={b.id} data-testid={`bin-row-${b.code}`}>
                <TableCell className="font-mono text-xs">{b.code}</TableCell>
                <TableCell>{b.warehouse_name}</TableCell>
                <TableCell><Badge variant="outline">{b.zone}</Badge></TableCell>
                <TableCell>{b.rack || "—"}</TableCell>
                <TableCell>{b.level || "—"}</TableCell>
                <TableCell className="text-right">{b.capacity}</TableCell>
              </TableRow>
            ))}
            {bins.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">No bins yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add bin</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Warehouse</Label>
              <Select value={form.warehouse_id} onValueChange={(v) => setForm({ ...form, warehouse_id: v })}>
                <SelectTrigger data-testid="bin-form-wh"><SelectValue placeholder="Pick…" /></SelectTrigger>
                <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="bin-form-code" placeholder="A-R06-L1" /></div>
              <div><Label>Zone</Label><Input value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })} placeholder="A" /></div>
              <div><Label>Rack</Label><Input value={form.rack} onChange={(e) => setForm({ ...form, rack: e.target.value })} placeholder="R06" /></div>
              <div><Label>Level</Label><Input value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} placeholder="L1" /></div>
              <div><Label>Capacity</Label><Input type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: parseFloat(e.target.value || "0") })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={!form.code || !form.warehouse_id} data-testid="bin-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
